/** @velyn-custom BEGIN — vely-pdp-horizontal-slider.js (shared UGC + accessories sliders) */

/**
 * @velyn-custom — currency consistency fix. Re-implements Shopify's standard
 * money-format substitution locally (same output as assets/global.js's
 * formatMoney for a given format string) instead of depending on a bare
 * `NextSkyTheme` global — this file loads both as a module (PDP/quickview)
 * and as a plain script (cart-drawer accessories), and `NextSkyTheme` is
 * never a true global in either case, so `NextSkyTheme.formatMoney(...)`
 * always silently fell through to a bare `(cents/100).toFixed(2)`
 * (no currency, period decimal).
 */
function applyVelyMoneyFormat(cents, format) {
  const value = Number(cents) || 0;
  const placeholderMatch = String(format).match(
    /\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/
  );
  const placeholder = placeholderMatch ? placeholderMatch[1] : "amount";

  let precision = 2;
  let thousands = ",";
  let decimal = ".";
  switch (placeholder) {
    case "amount_no_decimals":
      precision = 0;
      break;
    case "amount_with_comma_separator":
      thousands = ".";
      decimal = ",";
      break;
    case "amount_no_decimals_with_comma_separator":
      precision = 0;
      thousands = ".";
      decimal = ",";
      break;
    case "amount_with_apostrophe_separator":
      thousands = "'";
      break;
    case "amount_no_decimals_with_space_separator":
      precision = 0;
      thousands = " ";
      break;
    case "amount_with_space_separator":
      thousands = " ";
      decimal = ",";
      break;
    case "amount_with_period_and_space_separator":
      thousands = " ";
      break;
  }

  const negative = value < 0;
  const base = (Math.abs(value) / 100).toFixed(precision);
  const [integerPart, fractionPart = ""] = base.split(".");
  const withThousands = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousands
  );
  const formatted =
    (negative ? "-" : "") +
    withThousands +
    (precision > 0 ? decimal + fractionPart : "");

  return String(format).replace(
    /\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/,
    formatted
  );
}

function initVelyHorizontalSlider(host, config) {
  if (typeof host._velySliderCleanup === "function") {
    host._velySliderCleanup();
    host._velySliderCleanup = null;
  }

  const slider = host.querySelector(config.sliderSelector);
  const track = host.querySelector(config.trackSelector);
  const slides = [...host.querySelectorAll(config.slideSelector)];
  const nav = host.querySelector(config.navSelector);
  const prev = host.querySelector(config.prevSelector);
  const next = host.querySelector(config.nextSelector);

  if (!track || slides.length === 0) {
    return null;
  }

  const state = {
    host,
    slider,
    track,
    slides,
    nav,
    prev,
    next,
    index: 0,
    desktopSlides: Number(config.desktopSlides) || 3,
    mobileSlides: Number(config.mobileSlides) || 1.3,
    showNav: config.showNav !== false,
    onResize: null,
    onPrev: null,
    onNext: null,
    onTouchStart: null,
    onTouchEnd: null,
  };

  state.slidesVisible = () =>
    window.matchMedia("(min-width: 768px)").matches
      ? state.desktopSlides
      : state.mobileSlides;

  state.stepSize = () => {
    const slide = state.slides[0];
    if (!slide) return 212;
    const style = getComputedStyle(state.track);
    const gap = parseFloat(style.gap) || 12;
    const width = slide.offsetWidth;
    if (!width) return 212;
    return width + gap;
  };

  state.maxOffset = () => {
    const sliderWidth = state.slider?.clientWidth || 0;
    const trackWidth = state.track?.scrollWidth || 0;
    return Math.max(0, trackWidth - sliderWidth);
  };

  state.maxIndex = () => {
    const maxOffset = state.maxOffset();
    const step = state.stepSize();
    if (maxOffset <= 0 || step <= 0) return 0;
    return Math.ceil(maxOffset / step - 1e-6);
  };

  state.update = () => {
    const maxOffset = state.maxOffset();
    const max = state.maxIndex();
    if (state.index > max) state.index = max;
    if (state.index < 0) state.index = 0;

    const offset = Math.min(state.index * state.stepSize(), maxOffset);
    state.track.style.transform = `translate3d(-${offset}px, 0, 0)`;

    const needsNav = state.showNav && maxOffset > 1;

    if (state.nav) state.nav.hidden = !needsNav;
    if (state.prev) state.prev.disabled = state.index <= 0;
    if (state.next) state.next.disabled = state.index >= max;
  };

  state.onPrev = () => {
    if (state.index > 0) {
      state.index -= 1;
      state.update();
    }
  };
  state.onNext = () => {
    if (state.index < state.maxIndex()) {
      state.index += 1;
      state.update();
    }
  };

  state.prev?.addEventListener("click", state.onPrev);
  state.next?.addEventListener("click", state.onNext);

  let touchStartX = 0;
  state.onTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
  };
  state.onTouchEnd = (e) => {
    const dx = e.changedTouches[0].screenX - touchStartX;
    if (Math.abs(dx) < 40) return;
    if (dx < 0 && state.index < state.maxIndex()) {
      state.index += 1;
      state.update();
    } else if (dx > 0 && state.index > 0) {
      state.index -= 1;
      state.update();
    }
  };

  state.slider?.addEventListener("touchstart", state.onTouchStart, { passive: true });
  state.slider?.addEventListener("touchend", state.onTouchEnd, { passive: true });

  state.onResize = () => state.update();
  window.addEventListener("resize", state.onResize, { passive: true });
  if (typeof ResizeObserver === "function") {
    state.resizeObserver = new ResizeObserver(() => state.update());
    state.resizeObserver.observe(state.slider || state.host);
  }
  state.update();
  window.requestAnimationFrame(() => state.update());

  host._velySliderCleanup = () => {
    window.removeEventListener("resize", state.onResize);
    state.resizeObserver?.disconnect();
    state.prev?.removeEventListener("click", state.onPrev);
    state.next?.removeEventListener("click", state.onNext);
    state.slider?.removeEventListener("touchstart", state.onTouchStart);
    state.slider?.removeEventListener("touchend", state.onTouchEnd);
  };

  return state;
}

class VelyUgcGallery extends HTMLElement {
  connectedCallback() {
    this._source = this.dataset.source || "metafield";
    this._sliderState = null;

    if (this._source === "app") {
      this._initAppMode();
      return;
    }

    this._sliderState = initVelyHorizontalSlider(this, {
      sliderSelector: "[data-ugc-slider]",
      trackSelector: "[data-ugc-track]",
      slideSelector: "[data-ugc-slide]",
      navSelector: "[data-ugc-nav]",
      prevSelector: "[data-ugc-prev]",
      nextSelector: "[data-ugc-next]",
      desktopSlides: Number(this.dataset.desktopSlides) || 3,
      mobileSlides: Number(this.dataset.mobileSlides) || 1.3,
      showNav: this.dataset.showNav !== "false",
    });

    if (!this._sliderState) {
      this.remove();
    }
  }

  disconnectedCallback() {
    if (typeof this._velySliderCleanup === "function") {
      this._velySliderCleanup();
      this._velySliderCleanup = null;
    }
    this._sliderState = null;
  }

  _initAppMode() {
    const slot = this.querySelector("[data-ugc-app-slot]");
    const slider = this.querySelector("[data-ugc-slider]");

    const hasAppContent =
      slot &&
      (slot.children.length > 0 || (slot.textContent || "").trim().length > 0);

    if (!hasAppContent) {
      this.remove();
      return;
    }

    if (slider) slider.hidden = true;
    const nav = this.querySelector("[data-ugc-nav]");
    if (nav) nav.hidden = true;
  }
}

class VelyPdpAccessories extends HTMLElement {
  connectedCallback() {
    this._context = this.dataset.context || "pdp";
    this._initSlider();

    if (this._context === "cart") {
      this._bindCartCardUi();
      this._observeInjectedSlides();
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    if (typeof this._velySliderCleanup === "function") {
      this._velySliderCleanup();
      this._velySliderCleanup = null;
    }
    this._sliderState = null;
  }

  _sliderConfig() {
    return {
      sliderSelector: "[data-acc-slider]",
      trackSelector: "[data-acc-track]",
      slideSelector: "[data-acc-slide]",
      navSelector: "[data-acc-nav]",
      prevSelector: "[data-acc-prev]",
      nextSelector: "[data-acc-next]",
      desktopSlides: Number(this.dataset.desktopSlides) || 3,
      mobileSlides: Number(this.dataset.mobileSlides) || 1.3,
      showNav: this.dataset.showNav !== "false",
    };
  }

  _initSlider() {
    this._sliderState = initVelyHorizontalSlider(this, this._sliderConfig());
  }

  _observeInjectedSlides() {
    const track = this.querySelector("[data-acc-track]");
    if (!track || this._observer) return;

    this._observer = new MutationObserver(() => {
      if (this.querySelectorAll("[data-acc-slide]").length === 0) return;
      this._initSlider();
      this._bindCartCardUi();
    });
    this._observer.observe(track, { childList: true });
  }

  _bindCartCardUi() {
    this.querySelectorAll("[data-acc-card]").forEach((card) => {
      this._syncCartCard(card);
    });

    this.querySelectorAll("[data-acc-swatch]").forEach((swatchBtn) => {
      if (swatchBtn.dataset.velyAccSwatchBound === "true") return;
      swatchBtn.dataset.velyAccSwatchBound = "true";
      swatchBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const card = swatchBtn.closest("[data-acc-card]");
        if (card) this._updateCartVariant(card, swatchBtn);
        requestAnimationFrame(() => swatchBtn.blur());
      });
    });
  }

  _parsePercent(value) {
    if (value == null || value === "") return 0;
    const num = Number(String(value).trim().replace(/%/g, ""));
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.round(num);
  }

  // @velyn-custom — prefer the raw shop.money_format from [data-money-format]
  // (set on <vely-pdp-accessories> in snippets/vely-pdp-accessories.liquid,
  // same source the SSR `| money` filter uses) so this cart/PDP accessory
  // price re-render matches the initial page-load currency display.
  _formatMoney(cents) {
    const format =
      this.dataset.moneyFormat ||
      window.themeGlobalVariables?.settings?.money_format ||
      window.cartStrings?.money_format ||
      // Last-resort: any [data-money-format] on the page — guarantees a
      // currency symbol is never dropped even if this.dataset is unset.
      document.querySelector("[data-money-format]")?.getAttribute("data-money-format") ||
      "{{amount}}";
    return applyVelyMoneyFormat(cents, format);
  }

  _syncCartCard(card) {
    card.classList.remove("is-selected");
    const checkbox = card.querySelector(".vely-pdp-accessory__checkbox");
    if (checkbox) {
      card.classList.toggle("is-unavailable", checkbox.disabled);
    }
  }

  _updateCartVariant(card, swatchBtn) {
    const checkbox = card.querySelector(".vely-pdp-accessory__checkbox");
    const variantId = swatchBtn.dataset.variantId;
    const price = Number(swatchBtn.dataset.price) || 0;
    const compareAt = Number(swatchBtn.dataset.compareAt) || price;
    const available = String(swatchBtn.dataset.available).toLowerCase() !== "false";
    const salePercent = this._parsePercent(swatchBtn.dataset.salePercent);
    const currentPrice = card.querySelector("[data-acc-price]");
    const comparePrice = card.querySelector("[data-acc-compare]");
    const imageEl = card.querySelector(".vely-pdp-accessory__img");

    if (checkbox) {
      checkbox.value = variantId;
      checkbox.dataset.variantId = variantId;
      checkbox.dataset.price = String(price);
      checkbox.dataset.compareAt = String(compareAt);
      checkbox.dataset.salePercent = String(salePercent);
      checkbox.dataset.available = available ? "true" : "false";
      checkbox.disabled = !available;
      if (!available) checkbox.checked = false;
    }

    if (imageEl && swatchBtn.dataset.imageUrl) {
      imageEl.src = swatchBtn.dataset.imageUrl;
      if (swatchBtn.dataset.imageAlt) imageEl.alt = swatchBtn.dataset.imageAlt;
    }

    if (currentPrice) currentPrice.textContent = this._formatMoney(price);
    if (comparePrice) {
      const hasCompare = compareAt > price;
      comparePrice.textContent = hasCompare ? this._formatMoney(compareAt) : "";
      comparePrice.classList.toggle("hidden", !hasCompare);
    }

    const saleBadge = card.querySelector("[data-acc-sale-badge]");
    if (saleBadge) {
      saleBadge.textContent = salePercent > 0 ? `-${salePercent}%` : "";
      saleBadge.hidden = salePercent <= 0;
      saleBadge.classList.toggle("hidden", salePercent <= 0);
    }

    card.querySelectorAll("[data-acc-swatch]").forEach((el) => {
      const isCurrent = el === swatchBtn;
      el.classList.toggle("is-selected", isCurrent);
      el.setAttribute("aria-checked", isCurrent ? "true" : "false");
    });

    this._syncCartCard(card);
  }
}

if (!customElements.get("vely-ugc-gallery")) {
  customElements.define("vely-ugc-gallery", VelyUgcGallery);
}
if (!customElements.get("vely-pdp-accessories")) {
  customElements.define("vely-pdp-accessories", VelyPdpAccessories);
}
/** @velyn-custom END — vely-pdp-horizontal-slider.js */
